import type { ApiCredentialDto, ApiCredentialSecretsDto, StoreDto } from '@cartly/shared';
import { Alert, Badge, Button, PageHeader, Spinner, cx, useConfirm, useToast } from '@cartly/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../api/client';
import type { ConnectionInfo } from '../../api/types';
import { StatusBadge } from '../../components/Common';
import { useLoader } from '../../app/hooks';
import { useSession } from '../../app/session';
import { PLATFORM_LABELS } from './platforms';
import { StoreEvents, StoreProducts, StoreReceipts } from './StoreActivity';
import { CreateKeyModal, SecretsModal, StoreKeys } from './StoreKeys';
import { ShopifyPanel } from './ShopifyPanel';
import { StoreSetup } from './StoreSetup';

const TABS = [
  { id: 'setup', label: 'Setup' },
  { id: 'shopify', label: 'Shopify' },
  { id: 'keys', label: 'API keys' },
  { id: 'events', label: 'Events' },
  { id: 'products', label: 'Products' },
  { id: 'receipts', label: 'Webhook log' },
] as const;
type TabId = (typeof TABS)[number]['id'];

interface StoreData {
  store: StoreDto;
  info: ConnectionInfo;
  keys: ApiCredentialDto[] | null;
}

export function StoreDetailPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requestedTab = TABS.find((t) => t.id === params.get('tab'))?.id;
  const canKeys = can('api_keys:manage');
  const [creatingKey, setCreatingKey] = useState<'secret' | 'publishable' | null>(null);
  const [secrets, setSecrets] = useState<ApiCredentialSecretsDto | null>(null);

  const { data, error, loading, reload } = useLoader<StoreData>(async () => {
    const [store, info, keys] = await Promise.all([
      api.get<StoreDto>(`/stores/${id}`),
      api.get<ConnectionInfo>(`/stores/${id}/connection`),
      canKeys
        ? api.get<{ items: ApiCredentialDto[] }>(`/stores/${id}/credentials`).then((r) => r.items)
        : null,
    ]);
    return { store, info, keys };
  }, id);

  if (loading && !data) {
    return (
      <div className="page-loading">
        <Spinner size="lg" label="Loading store" />
      </div>
    );
  }
  if (error || !data) return <Alert tone="critical">{error ?? 'Store not found'}</Alert>;
  const { store, info, keys } = data;

  const disconnect = async () => {
    const ok = await confirm({
      title: `Disconnect ${store.name}?`,
      message:
        store.platform === 'shopify'
          ? 'Syncing pauses and incoming Shopify events are ignored. Your data is kept, and you can reconnect any time from this page.'
          : 'Its API keys stop working immediately. Synced data is kept.',
      confirmLabel: 'Disconnect',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/stores/${store.id}`);
      toast.success('Store disconnected');
      navigate('/stores');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const isShopify = store.platform === 'shopify';
  // Shopify stores authenticate with the app install, not API keys.
  const visibleTabs = TABS.filter((t) =>
    t.id === 'shopify' ? isShopify : t.id === 'keys' ? canKeys && !isShopify : true,
  );
  const tab: TabId =
    visibleTabs.find((t) => t.id === requestedTab)?.id ?? (isShopify ? 'shopify' : 'setup');

  const hasSecretKey = (keys ?? []).some((k) => k.kind === 'secret' && !k.revokedAt);

  return (
    <>
      <Link to="/stores" className="back-link">
        <ArrowLeft size={14} /> All stores
      </Link>
      <PageHeader
        title={
          <span className="row">
            {store.name}{' '}
            <StatusBadge
              status={store.status}
              label={store.status === 'pending' ? 'awaiting events' : store.status}
            />
          </span>
        }
        description={
          <span className="row">
            <Badge>{PLATFORM_LABELS[store.platform]}</Badge>
            <span className="mono">{store.domain}</span>
            <span>· {store.currency}</span>
          </span>
        }
        actions={
          can('stores:manage') &&
          store.status !== 'disconnected' && (
            <Button variant="secondary" onClick={() => void disconnect()}>
              Disconnect
            </Button>
          )
        }
      />
      <nav className="tabs settings-tabs" aria-label="Store sections">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={cx('tabs__tab tabs__tab--button', tab === t.id && 'tabs__tab--active')}
            aria-current={tab === t.id ? 'page' : undefined}
            onClick={() => setParams({ tab: t.id }, { replace: true })}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'shopify' && <ShopifyPanel storeId={store.id} />}
      {tab === 'setup' && (
        <StoreSetup
          store={store}
          info={info}
          hasSecretKey={hasSecretKey || !canKeys}
          onCreateKey={() => setCreatingKey('secret')}
        />
      )}
      {tab === 'keys' && keys && (
        <StoreKeys
          storeId={store.id}
          keys={keys}
          canManage={canKeys}
          onCreate={() => setCreatingKey('secret')}
          onChanged={(s) => {
            if (s) setSecrets(s);
            void reload();
          }}
        />
      )}
      {tab === 'events' && <StoreEvents storeId={store.id} canReplay={can('stores:manage')} />}
      {tab === 'products' && <StoreProducts storeId={store.id} />}
      {tab === 'receipts' && <StoreReceipts storeId={store.id} />}

      {creatingKey && (
        <CreateKeyModal
          storeId={store.id}
          defaultKind={creatingKey}
          onClose={() => setCreatingKey(null)}
          onCreated={(s) => {
            setCreatingKey(null);
            setSecrets(s);
            void reload();
          }}
        />
      )}
      {secrets && <SecretsModal secrets={secrets} onClose={() => setSecrets(null)} />}
    </>
  );
}
